import type { LeadFilter } from './leadFilters';

/**
 * Which dropdown owns which filter, and how much each one is holding.
 *
 * ── Why this file exists ───────────────────────────────────────────────────
 *
 * Kyu, 31 Aug, with the filter panel open on screen: "this is what I mean.
 * it's too noisy." Counted on that screenshot: 35 chips inside the disclosure
 * and 7 more in the Status row above it, 42 buttons drawn at the same weight
 * whether or not anybody uses them. Measured against the real stylesheet, the
 * bar stood 404px tall. As six dropdowns it stands 143px.
 *
 * Folding a control away is only safe while the page still says the filter is
 * on. The old bar had three mechanisms for that and the note in
 * LeadFilterBar.tsx said dropping any one of them made the pass worse than
 * not doing it. With everything behind a menu the promise gets stricter, not
 * looser, and it reduces to two:
 *
 *   1. every menu button carries a count while its axis is narrowing
 *   2. the active-filter row names every live filter, whatever is open
 *
 * This file is mechanism 1. It is a pure function so it can be tested without
 * mounting anything, and so the counts on the buttons cannot drift away from
 * the filter they claim to describe.
 *
 * ── The compile-time guarantee ─────────────────────────────────────────────
 *
 * Both tables below are `Record<keyof LeadFilter, …>`. Add a field to
 * LeadFilter and this file stops compiling until somebody says which menu owns
 * it and what "set" means for it. That is deliberate: the old
 * `hiddenFilterCount` was a hand-written sum, and when `noNextActionOnly`
 * shipped in Drop 1d nobody added it — the badge undercounted for weeks and no
 * test noticed, because the test was a hand-written list with the same gap.
 */
export type MenuKey = 'status' | 'category' | 'origin' | 'loop' | 'closed' | 'flags';

export const MENU_ORDER: MenuKey[] = ['status', 'category', 'origin', 'loop', 'closed', 'flags'];

export const MENU_LABELS: Record<MenuKey, string> = {
  status: 'Status',
  category: 'Category',
  origin: 'Lead Origin',
  loop: 'Follow-up',
  // "Closed because" and "Closed on" were two groups sitting apart in the old
  // panel. They only ever apply to closed leads, and two controls for one
  // situation is how you get one set and the other forgotten.
  closed: 'Closed',
  flags: 'Flags',
};

/**
 * Which menu each filter field lives in — or `null` for the ones that are not
 * in a menu at all.
 *
 * The four nulls are not oversights and each has a reason:
 *   query          the search box, which stays out in the open
 *   direction      the cohort sub-tabs
 *   noSourceOnly   the Needs Review sub-tab, same control as direction
 *   evidenceGroup  the evidence row, and only inside Needs Review
 *   onlyLeadIds    a drilled set, which gets its own banner above the table
 */
export const MENU_OF: Record<keyof LeadFilter, MenuKey | null> = {
  query: null,
  direction: null,
  noSourceOnly: null,
  evidenceGroup: null,
  onlyLeadIds: null,

  statusKeys: 'status',
  categoryIds: 'category',
  originGroup: 'origin',
  originId: 'origin',
  loop: 'loop',
  closedReasonIds: 'closed',
  closedWithinDays: 'closed',

  awaitingFirstTouchOnly: 'flags',
  nurtureOnly: 'flags',
  badContactOnly: 'flags',
  noNextActionOnly: 'flags',
  noOriginOnly: 'flags',
  noCaptureMethodOnly: 'flags',
  noCompanyOnly: 'flags',
  staleAfterDays: 'flags',
  createdWithinDays: 'flags',
};

/**
 * How much each field is narrowing by — 0 when it is not set.
 *
 * `originId` returns 0 on purpose, and it is the one entry worth reading
 * twice. "Social media" and "Social media, Instagram" are ONE narrowing made
 * in two steps; counting the platform separately would make the button say 2
 * for a single decision. The old `hiddenFilterCount` carried the same rule and
 * the same note.
 */
const NARROWING: Record<keyof LeadFilter, (filter: LeadFilter) => number> = {
  query: () => 0,
  direction: () => 0,
  noSourceOnly: () => 0,
  evidenceGroup: () => 0,
  // Zero for the same reason `query` is zero: it is not hidden behind a menu
  // button, so counting it there would put a 1 on a button that opens nothing
  // about it. The banner above the table is where a drilled set announces
  // itself, and it is louder than a chip.
  onlyLeadIds: () => 0,

  statusKeys: (filter) => filter.statusKeys.length,
  categoryIds: (filter) => filter.categoryIds.length,
  originGroup: (filter) => (filter.originGroup === null ? 0 : 1),
  originId: () => 0,
  loop: (filter) => (filter.loop === null ? 0 : 1),
  closedReasonIds: (filter) => filter.closedReasonIds.length,
  closedWithinDays: (filter) => (filter.closedWithinDays === null ? 0 : 1),

  awaitingFirstTouchOnly: (filter) => (filter.awaitingFirstTouchOnly ? 1 : 0),
  nurtureOnly: (filter) => (filter.nurtureOnly ? 1 : 0),
  badContactOnly: (filter) => (filter.badContactOnly ? 1 : 0),
  noNextActionOnly: (filter) => (filter.noNextActionOnly ? 1 : 0),
  noOriginOnly: (filter) => (filter.noOriginOnly ? 1 : 0),
  noCaptureMethodOnly: (filter) => (filter.noCaptureMethodOnly ? 1 : 0),
  noCompanyOnly: (filter) => (filter.noCompanyOnly ? 1 : 0),
  staleAfterDays: (filter) => (filter.staleAfterDays === null ? 0 : 1),
  createdWithinDays: (filter) => (filter.createdWithinDays === null ? 0 : 1),
};

/** What each dropdown button should show beside its name. 0 means no badge. */
export function menuCounts(filter: LeadFilter): Record<MenuKey, number> {
  const counts: Record<MenuKey, number> = {
    status: 0,
    category: 0,
    origin: 0,
    loop: 0,
    closed: 0,
    flags: 0,
  };
  for (const field of Object.keys(NARROWING) as (keyof LeadFilter)[]) {
    const menu = MENU_OF[field];
    if (menu !== null) {
      counts[menu] += NARROWING[field](filter);
    }
  }
  return counts;
}
